import * as React from "react";

import {DEFAULT_SORT_ORDER} from "./constants.ts";
import type {Column, Sort} from "./types.ts";
import {sortData} from "./utils.ts";

/**
 * Get the initial sort for the table (if the table is sortable).
 *
 * @param {Array<Column<T>>} columns - The table columns.
 * @param {string} defaultSortKey - The sort key to use for the initial sort.
 *
 * @returns {?Sort} The initial sort if the conditions are met. Otherwise, null.
 */
function getInitialSort<T>(
    columns: Array<Column<T>>,
    defaultSortKey?: string,
): Sort | null | undefined {
    const sortableColumns = columns.filter((column) => column.sortable);

    // If there are no sortable columns, return an empty sort.
    if (sortableColumns.length === 0 && !defaultSortKey) {
        return;
    }

    // Use the first sortable column as the default sort column.
    if (!defaultSortKey) {
        return {
            order: sortableColumns[0].sortable ?? DEFAULT_SORT_ORDER,
            orderBy: sortableColumns[0].dataKey ?? "",
        };
    }

    // Unless a default sort key is provided, use the column associated with the
    // default sort key.
    const defaultSortColumn = sortableColumns.find(
        (column) => column.dataKey === defaultSortKey,
    );

    return {
        order: defaultSortColumn?.sortable ?? DEFAULT_SORT_ORDER,
        orderBy: defaultSortKey,
    };
}

/**
 * Sort the data if a sort object is provided.
 *
 * @param {Array<T>} data - The data to sort.
 * @param {?Sort} sort - The sort object.
 *
 * @returns {Array<T>} The sorted data if a sort object is provided. Otherwise,
 * return the data as-is.
 */
function maybeSortData<T>(data: Array<T>, sort?: Sort | null): Array<T> {
    if (!sort) {
        return data;
    }

    return sortData(data, sort);
}

/**
 * Custom hook to handle sorting by a single column.
 */
export default function useSortable<T>(
    data: Array<T>,
    columns: Array<Column<T>>,
    defaultSortKey?: string,
): {
    sortedData: Array<T>;
    sort: Sort | null | undefined;
    onSort: (nextSort: Sort) => void;
} {
    const initialSort = React.useMemo(
        () => getInitialSort(columns, defaultSortKey),
        [columns, defaultSortKey],
    );

    const [sort, setSort] = React.useState<Sort | null | undefined>(
        initialSort,
    );
    const sortedData = React.useMemo(
        () => maybeSortData(data, sort),
        [data, sort],
    );

    /**
     * Handle sorting by a single column.
     *
     * Note that if the column is already being sorted, the sort order will be
     * toggled. Otherwise, the column will be sorted using the default sort
     * order.
     *
     * @param {string} orderBy - The key/accessor to sort by.
     */
    const onSort = (nextSort: Sort) => {
        setSort((prevSort) => {
            // If the column is already being sorted, toggle the order.
            if (prevSort && prevSort?.orderBy === nextSort.orderBy) {
                return {
                    orderBy: prevSort?.orderBy,
                    order:
                        prevSort?.order === "descending"
                            ? "ascending"
                            : "descending",
                };
            }

            // Otherwise, sort by the new column using the default sort
            // order provided by the column.
            return nextSort;
        });
    };

    return {
        sortedData,
        sort,
        onSort,
    };
}
